import React, { useEffect, useState } from "react";
import axios from "axios";
import Utils from "../Utils";

const getNodeByPath = (root, pathArr) => {
  let node = root;
  for (const part of pathArr) {
    if (!node) return null;
    const found = node.folders?.find((f) => f.name === part);
    if (!found) return null;
    node = found;
  }
  return node;
};

const Breadcrumbs = ({ path, onNavigate }) => (
  <nav className="mb-4 flex items-center gap-2 text-sm">
    <span
      className={path.length === 0 ? "font-bold" : "cursor-pointer text-blue-600 hover:underline"}
      onClick={() => path.length > 0 && onNavigate([])}
    >
      Root
    </span>
    {path.map((part, idx) => (
      <React.Fragment key={part + idx}>
        <span key={"sep-" + idx}>/</span>
        <span
          key={part}
          className={idx === path.length - 1 ? "font-bold" : "cursor-pointer text-blue-600 hover:underline"}
          onClick={() => idx !== path.length - 1 && onNavigate(path.slice(0, idx + 1))}
        >
          {part}
        </span>
      </React.Fragment>
    ))}
  </nav>
);

const FolderGrid = ({ folders, files, onFolderClick }) => (
  <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
    {folders?.map((folder) => (
      <div
        key={folder.name}
        className="flex flex-col items-center p-4 bg-gray-50 rounded cursor-pointer hover:bg-blue-50"
        onClick={() => onFolderClick(folder.name)}
      >
        <i className="fas fa-folder text-yellow-500 text-3xl mb-2"></i>
        <span className="truncate w-full text-center font-medium" style={{ maxWidth: 120, display: "inline-block" }} title={folder.name}>
          {folder.name}
        </span>
      </div>
    ))}
    {files?.map((file) => {
      let iconClass = "fa-file text-gray-400";
      if (file.name.toLowerCase().endsWith(".pdf")) iconClass = "fa-file-pdf text-red-600";
      else if (file.name.toLowerCase().endsWith(".docx")) iconClass = "fa-file-word text-blue-600";
      else if (file.name.toLowerCase().endsWith(".xlsx")) iconClass = "fa-file-excel text-green-600";
      return (
        <div key={file.name} className="flex flex-col items-center p-4 bg-gray-50 rounded">
          <a
            href={Utils.apiUrl(`admin/resources/download?url=${encodeURIComponent(file.url)}&name=${encodeURIComponent(file.name)}`)}
            className="flex flex-col items-center"
          >
            <i className={`fas ${iconClass} text-3xl mb-2`}></i>
            <span className="truncate w-full text-center" style={{ maxWidth: 120, display: "inline-block" }} title={file.name}>
              {file.name}
            </span>
            <span className="text-xs text-gray-400">{(file.size / 1024 / 1024).toFixed(2)} MB</span>
          </a>
        </div>
      );
    })}
  </div>
);

const Resources = () => {
  const [list, setList] = useState({
    loading: false,
    error: false,
    pageInfo: {},
    data: null,
  });
  const [path, setPath] = useState([]);

  const fetchResources = () => {
    setList((prev) => ({ ...prev, loading: true }));
    axios
      .get(Utils.apiUrl("resources.json"), Utils.apiHeaders())
      .then((res) => {
        if (res.data.success) {
          setList((prev) => ({
            ...prev,
            loading: false,
            error: false,
            pageInfo: res.data.pageInfo || {},
            data: res.data.data,
          }));
        } else {
          setList((prev) => ({
            ...prev,
            loading: false,
            error: res.data.message || "Failed to load",
            pageInfo: {},
            data: null,
          }));
        }
      })
      .catch((err) => {
        setList((prev) => ({
          ...prev,
          loading: false,
          error: err.message || "Server error",
          data: null,
        }));
      });
  };

  useEffect(() => {
    fetchResources();
  }, []);

  let node = list.data;
  if (path.length > 0 && node) {
    node = getNodeByPath(node, path);
  }

  if (list.loading) {
    return <div className="p-6">Loading resources...</div>;
  }
  if (list.error) {
    return <div className="p-6 text-red-600">Error loading resources: {list.error}</div>;
  }
  if (!node) {
    return <div className="p-6">No resources found.</div>;
  }

  return (
    <div className="p-6 bg-white rounded shadow">
      <h1 className="text-2xl font-bold mb-4 flex items-center gap-2">
        <i className="fas fa-folder-tree"></i>
        Photography Resource Hub
      </h1>
      <Breadcrumbs path={path} onNavigate={setPath} />
      <FolderGrid folders={node.folders} files={node.files} onFolderClick={(folderName) => setPath([...path, folderName])} />
    </div>
  );
};

export default Resources;
